// Calcolo dei contributi previdenziali per titolari di partita IVA.
//
// La logica di base e' quella usata dal calcolatore forfettario
// (js/forfettario.js), qui estratta in una funzione pura e riutilizzabile: il
// regime forfettario e quello ordinario calcolano i contributi allo stesso
// modo, cambia solo la base imponibile previdenziale (forfettario: fatturato x
// coefficiente; ordinario: ricavi - costi).
//
// Rispetto al forfettario e' modellato anche il MASSIMALE contributivo: nel
// forfettario e' irrilevante (85.000 x 78% = 66.300, sempre sotto il tetto),
// nell'ordinario no, ed e' proprio la fascia di reddito che usa questo regime.
//
// gestione = un elemento di data/forfettario.json -> gestioni_previdenziali.
//
// Ritorna:
//   - contributi:            totale versato all'ente previdenziale.
//   - contributiDeducibili:  quota deducibile dal reddito imponibile IRPEF.
//     Coincide con `contributi` tranne per l'integrativo delle casse che,
//     essendo addebitato in rivalsa al cliente, non e' un costo del
//     professionista e quindi non e' deducibile.
//   - contributoIntegrativo: quota integrativa dovuta alla cassa.
//   - integrativoRivalsa:    parte dell'integrativo effettivamente recuperata
//     dal cliente in fattura. Coincide con l'integrativo salvo quando scatta il
//     minimo: la differenza tra minimo e percentuale sul volume d'affari resta
//     a carico del professionista.
//   - contributiACarico:     quanto esce davvero di tasca (contributi meno la
//     parte di integrativo ribaltata sul cliente). E' questo, non `contributi`,
//     a ridurre il netto.
//   - imponibilePrevidenziale: base effettivamente assoggettata a contribuzione
//     (cioe' il reddito eventualmente tagliato dal massimale).
//   - massimaleApplicato: true se il massimale ha morso.

const clampInt = (value, min, max, fallback) => {
    const n = parseInt(value, 10);
    if (!Number.isInteger(n)) return fallback;
    return Math.min(Math.max(n, min), max);
};

// Massimale annuo di reddito imponibile ai fini contributivi. Non e'
// frazionabile a mese, quindi non segue il pro-rata dei mesi di attivita'.
// Per Artigiani/Commercianti il tetto e' piu' basso per chi ha anzianita'
// contributiva al 31/12/1995.
export function getMassimaleContributivo(gestione, params = {}) {
    if (!gestione) return Infinity;
    const ante1996 = Boolean(params.ante1996);
    if (ante1996 && gestione.massimale_reddito_ante1996 != null) {
        return gestione.massimale_reddito_ante1996;
    }
    return gestione.massimale_reddito ?? Infinity;
}

// imponibileContributi: base previdenziale (reddito: ricavi - costi).
// baseRivalsa:          volume d'affari lordo (ricavi/provvigioni), usato per
//                       l'integrativo delle casse e per il massimale ENASARCO.
export function calcolaContributiPrevidenziali(imponibileContributi, baseRivalsa, gestione, params = {}) {
    const mesiAttivita = clampInt(params.mesiAttivita ?? 12, 1, 12, 12);
    const numeroMandanti = clampInt(params.numeroMandanti ?? 1, 1, 50, 1);
    const nuovaAttivitaArtCom = Boolean(params.nuovaAttivitaArtCom);
    const riduzione50Richiesta = Boolean(params.riduzione50);
    // Riduzione 35% riservata ai forfettari iscritti ad Artigiani/Commercianti
    // (L. 190/2014 art. 1 c. 77). Non esiste in regime ordinario: il motore
    // ordinario semplicemente non passa questo parametro.
    const riduzione35Richiesta = Boolean(params.riduzione35);
    const dipendenteFullTime = Boolean(params.dipendenteFullTime);
    const riduzioneDipendente = Boolean(params.riduzioneDipendente);

    // Nel regime ordinario non esiste il tetto di reddito da lavoro dipendente
    // (35.000 EUR) previsto per l'accesso al forfettario: la riduzione
    // contributiva del dipendente gia' assicurato altrove si applica sempre.
    const riduzioneDipendenteAttiva = riduzioneDipendente && dipendenteFullTime;

    // Massimale: oltre questa soglia il reddito non e' piu' assoggettato.
    const massimale = getMassimaleContributivo(gestione, params);
    const imponibile = Math.min(Math.max(0, imponibileContributi), massimale);
    const massimaleApplicato = Number.isFinite(massimale) && imponibileContributi > massimale;

    let contributi = 0;
    let contributiDeducibili = null;
    let contributoIntegrativo = 0;
    let integrativoRivalsa = 0;
    let minimaleFisso = 0; // parte in rate fisse (minimale art/com), esclusa dall'acconto

    if (gestione?.tipo === 'CASSA_CON_MINIMI') {
        // Casse professionali: soggettivo (con minimo, e spesso con aliquota
        // ridotta oltre un limite reddituale) + integrativo sul volume d'affari
        // + eventuale supplementare, solidarieta' e maternita'.
        const limite = gestione.limite_reddito_soggettivo ?? Infinity;
        const aliquotaOltre = gestione.aliquota_soggettivo_oltre ?? 0;
        const soggettivoCalcolato =
            Math.min(imponibile, limite) * ((gestione.aliquota_soggettivo ?? 0) / 100) +
            Math.max(0, imponibile - limite) * (aliquotaOltre / 100);
        const contributoSoggettivo = Math.max(soggettivoCalcolato, gestione.minimo_soggettivo ?? 0);

        // Alcune casse (es. CNPR) affiancano un soggettivo supplementare.
        const aliquotaSuppl = gestione.aliquota_soggettivo_supplementare ?? 0;
        const minimoSuppl = gestione.minimo_soggettivo_supplementare ?? 0;
        const contributoSupplementare = (aliquotaSuppl > 0 || minimoSuppl > 0)
            ? Math.max(imponibile * (aliquotaSuppl / 100), minimoSuppl)
            : 0;

        // Integrativo: percentuale sul volume d'affari, con un minimo. La parte
        // "a percentuale" viene ribaltata in fattura al cliente; l'eventuale
        // scarto fino al minimo resta a carico del professionista.
        const aliquotaIntegrativo = gestione.aliquota_integrativo ?? 0;
        const integrativoPercentuale = baseRivalsa * (aliquotaIntegrativo / 100);
        contributoIntegrativo = Math.max(integrativoPercentuale, gestione.minimo_integrativo ?? 0);
        integrativoRivalsa = Math.min(contributoIntegrativo, integrativoPercentuale);

        const aliquotaSolid = gestione.aliquota_solidarieta ?? 0;
        const minimoSolid = gestione.minimo_solidarieta ?? 0;
        const contributoSolidarieta = (aliquotaSolid > 0 || minimoSolid > 0)
            ? Math.max(imponibile * (aliquotaSolid / 100), minimoSolid)
            : 0;

        const contributoMaternita = gestione.contributo_maternita ?? 0;

        contributi = contributoSoggettivo + contributoSupplementare + contributoIntegrativo +
            contributoSolidarieta + contributoMaternita;
        contributiDeducibili = contributoSoggettivo + contributoSupplementare +
            contributoSolidarieta + contributoMaternita;
    } else if (gestione?.tipo === 'ENPAM') {
        // ENPAM: Quota A fissa per fascia d'eta' (copre gia' una quota di
        // reddito) + Quota B percentuale sul solo reddito eccedente.
        const fasce = gestione.quota_a || {};
        const fascia = fasce[params.fasciaEtaEnpam] != null ? params.fasciaEtaEnpam : 'over40';
        const quotaA = fasce[fascia] ?? 0;
        const franchigie = gestione.franchigia_quota_b || {};
        const franchigia = fascia === 'over40'
            ? (franchigie.over40 ?? 0)
            : (franchigie.under40 ?? 0);
        const baseQuotaB = Math.max(0, imponibile - franchigia);
        const limiteB = gestione.limite_quota_b ?? Infinity;
        const quotaB =
            Math.min(baseQuotaB, limiteB) * ((gestione.aliquota_quota_b ?? 0) / 100) +
            Math.max(0, baseQuotaB - limiteB) * ((gestione.aliquota_quota_b_oltre ?? 0) / 100);
        // ENPAM non ha contributo integrativo: tutto e' deducibile.
        contributi = quotaA + quotaB + (gestione.contributo_maternita ?? 0);
    } else if (gestione?.tipo === 'MISTO_CON_MINIMALE') {
        const proRataFactor = mesiAttivita / 12;
        const minimale = (gestione.minimale_reddito || 0) * proRataFactor;
        const fisso = (gestione.costo_fisso_annuo || 0) * proRataFactor;
        minimaleFisso = fisso;
        const eccedenza = Math.max(imponibile - minimale, 0);
        const contributiBase = fisso + (eccedenza * ((gestione.aliquota_eccedenza || 0) / 100));
        let contributoSolidarieta = 0;
        const sogliaSolidarieta = gestione.soglia_solidarieta || 0;
        if (sogliaSolidarieta > 0 && imponibile > sogliaSolidarieta) {
            contributoSolidarieta = (imponibile - sogliaSolidarieta) * 0.01;
        }
        contributi = contributiBase + contributoSolidarieta;
    } else if (gestione?.tipo === 'ENASARCO') {
        // Massimale e minimale ENASARCO si applicano per rapporto di agenzia:
        // il plurimandatario li moltiplica per il numero di mandanti.
        const aliquotaAgente = (gestione.aliquota_agente || 0) / 100;
        const perMandante = Boolean(gestione.mandanti_multipli);
        const mandanti = perMandante ? numeroMandanti : 1;
        const massimaleUnitario = perMandante
            ? (gestione.massimale_provvigioni_per_mandante ?? Infinity)
            : (gestione.massimale_provvigioni ?? Infinity);
        const massimaleProvvigioni = massimaleUnitario * mandanti;
        const minimaleAgente = (gestione.minimale_annuo_agente || 0) * mandanti;
        const baseCalcolo = Math.min(baseRivalsa, massimaleProvvigioni);
        const contributoCalcolato = baseCalcolo * aliquotaAgente;
        contributi = Math.max(contributoCalcolato, minimaleAgente);
    } else {
        // PERCENTUALE_PURA (INPS Gestione Separata, casse professionali "pure").
        let aliquotaContributiva = gestione ? (gestione.aliquota || 0) : 0;
        if (riduzioneDipendenteAttiva && gestione?.id === 'INPS_GS') {
            aliquotaContributiva = 24.0;
        }
        contributi = (imponibile * (aliquotaContributiva / 100));
    }

    // Dipendente full-time gia' assicurato: esenzione dai contributi minimi
    // Artigiani/Commercianti (si versa solo sull'eccedenza, qui azzerata perche'
    // il minimale non e' dovuto).
    if (riduzioneDipendenteAttiva && ['INPS_ART', 'INPS_COM'].includes(gestione?.id || '')) {
        contributi = 0;
        minimaleFisso = 0;
    }

    // Riduzione 50% per nuove iscrizioni Artigiani/Commercianti (art. 1 c. 186
    // L. 207/2024): riservata a chi si e' iscritto per la prima volta nel 2025,
    // per 36 mesi. NON e' stata prorogata per le iscrizioni dal 2026. La
    // riduzione 35% del forfettario non e' disponibile in regime ordinario.
    if (!riduzioneDipendenteAttiva && ['INPS_ART', 'INPS_COM'].includes(gestione?.id || '')) {
        // Le due riduzioni non sono cumulabili: la 50% prevale se richiesta.
        if (riduzione50Richiesta && nuovaAttivitaArtCom) {
            contributi = contributi * 0.50;
            minimaleFisso = minimaleFisso * 0.50;
        } else if (riduzione35Richiesta && gestione?.riduzione_forfettario_disponibile) {
            contributi = contributi * 0.65;
            minimaleFisso = minimaleFisso * 0.65;
        }
    }

    // Parte dei contributi soggetta a saldo + acconto (come l'IRPEF) vs parte in
    // rate fisse. Gestione Separata: tutto ad acconto. Artigiani/Commercianti:
    // solo l'eccedenza sopra il minimale (il fisso e' in 4 rate fisse). Casse
    // professionali/ENASARCO: acconti con regole proprie, non modellati.
    const id = gestione?.id || '';
    let contributiFissi = 0;
    let contributiAcconto = 0;
    if (id === 'INPS_GS') {
        contributiAcconto = contributi;
    } else if (['INPS_ART', 'INPS_COM'].includes(id)) {
        contributiFissi = minimaleFisso;
        contributiAcconto = Math.max(0, contributi - minimaleFisso);
    }

    return {
        contributi,
        contributiDeducibili: contributiDeducibili ?? contributi,
        contributoIntegrativo,
        integrativoRivalsa,
        contributiACarico: Math.max(0, contributi - integrativoRivalsa),
        contributiAcconto,
        contributiFissi,
        imponibilePrevidenziale: imponibile,
        massimale,
        massimaleApplicato
    };
}
